import React from 'react';
import { Box, LinearProgress, Typography, CircularProgress } from '@mui/material';
import { CloudUpload, Compress, CheckCircle, Error } from '@mui/icons-material';

/**
 * UploadProgressIndicator - Shows upload and compression progress
 *
 * Props:
 * - uploadProgress: number (0-100) - Upload phase progress
 * - compressionProgress: number (0-100) - Compression phase progress
 * - phase: 'idle' | 'uploading' | 'compressing' | 'complete' | 'error'
 * - error: string | null - Error message if phase is 'error'
 * - compact: boolean - Use compact layout
 * - hideCompression: boolean - Hide compression progress bar (for image uploads without compression)
 */
const UploadProgressIndicator = ({
  uploadProgress = 0,
  compressionProgress = 0,
  phase = 'idle',
  error = null,
  compact = false,
  hideCompression = false
}) => {
  if (phase === 'idle') {
    return null;
  }

  const getPhaseIcon = () => {
    switch (phase) {
      case 'uploading':
        return <CloudUpload sx={{ color: '#3B82F6', fontSize: compact ? 20 : 24 }} />;
      case 'compressing':
        return <Compress sx={{ color: '#8B5CF6', fontSize: compact ? 20 : 24 }} />;
      case 'complete':
        return <CheckCircle sx={{ color: '#10B981', fontSize: compact ? 20 : 24 }} />;
      case 'error':
        return <Error sx={{ color: '#EF4444', fontSize: compact ? 20 : 24 }} />;
      default:
        return <CircularProgress size={compact ? 16 : 20} />;
    }
  };

  const getPhaseLabel = () => {
    switch (phase) {
      case 'uploading':
        return 'מעלה...';
      case 'compressing':
        return 'מעבד...';
      case 'complete':
        return 'הושלם';
      case 'error':
        return 'שגיאה';
      default:
        return '';
    }
  };

  const getProgressColor = () => {
    switch (phase) {
      case 'uploading':
        return '#3B82F6';
      case 'compressing':
        return '#8B5CF6';
      case 'complete':
        return '#10B981';
      case 'error':
        return '#EF4444';
      default:
        return '#94A3B8';
    }
  };

  const currentProgress = phase === 'uploading' ? uploadProgress : compressionProgress;

  if (compact) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1,
          backgroundColor: phase === 'error' ? '#FEF2F2' : '#F8FAFC',
          borderRadius: 1,
          border: `1px solid ${phase === 'error' ? '#FECACA' : '#E2E8F0'}`
        }}
      >
        {getPhaseIcon()}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography sx={{ fontSize: 12, color: '#64748B' }}>
              {getPhaseLabel()}
            </Typography>
            {phase !== 'complete' && phase !== 'error' && (
              <Typography sx={{ fontSize: 12, color: '#64748B', fontWeight: 500 }}>
                {currentProgress}%
              </Typography>
            )}
          </Box>
          {phase !== 'complete' && phase !== 'error' && (
            <LinearProgress
              variant="determinate"
              value={currentProgress}
              sx={{
                height: 4,
                borderRadius: 2,
                backgroundColor: '#E2E8F0',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: getProgressColor(),
                  borderRadius: 2
                }
              }}
            />
          )}
          {error && (
            <Typography sx={{ fontSize: 11, color: '#EF4444', mt: 0.5 }}>
              {error}
            </Typography>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 2,
        backgroundColor: phase === 'error' ? '#FEF2F2' : '#F8FAFC',
        borderRadius: 2,
        border: `1px solid ${phase === 'error' ? '#FECACA' : '#E2E8F0'}`
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        {getPhaseIcon()}
        <Typography sx={{ fontSize: 14, fontWeight: 500, color: '#1E293B' }}>
          {getPhaseLabel()}
        </Typography>
        {phase !== 'complete' && phase !== 'error' && (
          <Typography sx={{ fontSize: 14, color: '#64748B', ml: 'auto' }}>
            {currentProgress}%
          </Typography>
        )}
      </Box>

      {/* Upload Progress */}
      <Box sx={{ mb: 1.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography sx={{ fontSize: 12, color: '#64748B' }}>
            העלאה
          </Typography>
          <Typography sx={{ fontSize: 12, color: '#64748B' }}>
            {uploadProgress}%
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={uploadProgress}
          sx={{
            height: 6,
            borderRadius: 3,
            backgroundColor: '#E2E8F0',
            '& .MuiLinearProgress-bar': {
              backgroundColor: uploadProgress === 100 ? '#10B981' : '#3B82F6',
              borderRadius: 3
            }
          }}
        />
      </Box>

      {/* Compression Progress */}
      {!hideCompression && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography sx={{ fontSize: 12, color: '#64748B' }}>
              עיבוד ודחיסה
            </Typography>
            <Typography sx={{ fontSize: 12, color: '#64748B' }}>
              {compressionProgress}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={compressionProgress}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: '#E2E8F0',
              '& .MuiLinearProgress-bar': {
                backgroundColor: compressionProgress === 100 ? '#10B981' : '#8B5CF6',
                borderRadius: 3
              }
            }}
          />
        </Box>
      )}

      {error && (
        <Typography sx={{ fontSize: 12, color: '#EF4444', mt: 1.5 }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default UploadProgressIndicator;
